import {
    BrowseCourseCard,
    BrowseCourseCarousel,
    CAROUSEL_CONFIGURATIONS,
} from '@udemy-v2/react-browse-course';
import {useDeviceType} from '@udemy-v2/hooks';
import styles from './more-courses-by-instructors.module.scss';
import {LocalizedHtml, useI18n} from '@udemy-v2/i18n';
import {
    BrowseEventTrackingProvider,
    BackendSourceOptions,
    TrackableDiscoveryItem,
    DiscoveryItemTypeOptions,
} from '@udemy-v2/react-browse-event-tracking';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useMoreCoursesByInstructor} from '@/lib/hooks/use-more-courses-by-instructor';
import {BrowseCourseWithInstructorDetails} from '@/types/course';
import {Instructor} from '@udemy-v2/browse-course';

export interface MoreCoursesByInstructorProps {
    instructors: Instructor[];
}

export interface MoreCoursesByInstructorsProps {
    instructor: Instructor;
    moreCoursesByInstructor: TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[];
}

/**
 * Device-responsive course list component (matches monolith behavior)
 * Mobile: Vertical list of individual course cards
 * Desktop: BrowseCourseCarousel (matches original V2 implementation)
 */
const InstructorCoursesList = ({
    courses,
}: {
    courses: TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[];
}) => {
    const deviceType = useDeviceType();

    if (deviceType === 'mobile') {
        return (
            <div>
                {courses.map((course, index) => (
                    <BrowseEventTrackingProvider
                        key={course.id}
                        browseEventTrackingContext={{index}}
                    >
                        <div className={styles['card-container']}>
                            <BrowseCourseCard
                                course={course}
                                layout="condensed"
                                options={{openInNewTab: true}}
                            />
                        </div>
                    </BrowseEventTrackingProvider>
                ))}
            </div>
        );
    }

    return (
        <BrowseCourseCarousel
            courses={courses}
            className={styles['margin-top-xs']}
            configuration={{
                ...CAROUSEL_CONFIGURATIONS.CARDS_FULL_WIDTH,
                carouselProps: {
                    allowScroll: true,
                },
            }}
            options={{openInNewTab: true}}
        />
    );
};

export const MoreCoursesByInstructors = ({instructors}: MoreCoursesByInstructorProps) => {
    const coursesByInstructor = useMoreCoursesByInstructor();
    return (
        <>
            {instructors.map((instructor) => {
                if (!instructor.url) return null;
                return (
                    <MoreCoursesByInstructor
                        key={`MoreCoursesByInstructors-${instructor.url}`}
                        instructor={instructor}
                        moreCoursesByInstructor={coursesByInstructor[instructor.url] ?? []}
                    />
                );
            })}
        </>
    );
};

const MoreCoursesByInstructor = ({
    instructor,
    moreCoursesByInstructor,
}: MoreCoursesByInstructorsProps) => {
    const {gettext, interpolate} = useI18n();
    const course = useClpCourse();

    if (moreCoursesByInstructor.length === 0) return null;

    const displayedCourses =
        moreCoursesByInstructor.length > 3
            ? moreCoursesByInstructor.slice(0, 3)
            : moreCoursesByInstructor;

    return (
        <div key={`MoreCoursesByInstructor-${instructor.url}`} className={styles['margin-top-xs']}>
            <LocalizedHtml
                html={interpolate(
                    gettext('More Courses by <a class="instructorLink">%(instructorName)s</a>'),
                    {
                        instructorName: instructor.name,
                    },
                    true,
                )}
                interpolate={{
                    instructorLink: (
                        <a className={'ud-heading-xl ud-link-underline'} href={instructor.url} />
                    ),
                }}
                className="ud-heading-xl"
            />
            <BrowseEventTrackingProvider
                browseEventTrackingContext={{
                    relatedSourceId: course.id,
                    relatedSourceType: DiscoveryItemTypeOptions.COURSE,
                    backendSource: BackendSourceOptions.TAUGHT_COURSES,
                }}
            >
                <InstructorCoursesList courses={displayedCourses} />
            </BrowseEventTrackingProvider>
        </div>
    );
};
